import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Utensils,
  FolderTree,
  Gift,
  Stamp,
  Award,
  CalendarCheck,
  UserCog,
  Store,
  Users,
  Crown,
  UserPlus,
  Percent,
  Package,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { getMerchantActivity } from "@/lib/api/admin";
import type {
  MerchantActivity,
  MerchantActivityAction,
  MerchantActivityEntityType,
} from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { formatDateTime } from "@/lib/utils/date";
import { formatInt } from "@/lib/utils/number";

const PAGE_SIZE = 20;

// Singleton entities (one per store) — never quote a name for these.
const SINGLETON_ENTITY_TYPES = new Set<MerchantActivityEntityType>([
  "store_profile",
  "stamp_card",
  "store_referral_program",
]);

const ENTITY_CONFIG: Record<MerchantActivityEntityType, { noun: string; Icon: LucideIcon }> = {
  menu_item: { noun: "menu item", Icon: Utensils },
  menu_category: { noun: "menu category", Icon: FolderTree },
  point_reward: { noun: "point reward", Icon: Gift },
  stamp_card: { noun: "stamp card", Icon: Stamp },
  stamp_reward: { noun: "stamp reward", Icon: Award },
  event: { noun: "event", Icon: CalendarCheck },
  staff: { noun: "staff member", Icon: UserCog },
  store_profile: { noun: "store profile", Icon: Store },
  manager: { noun: "manager", Icon: Users },
  owner: { noun: "owner", Icon: Crown },
  store_referral_program: { noun: "referral program", Icon: UserPlus },
  target_deal: { noun: "target deal", Icon: Percent },
  bundle_deal: { noun: "bundle deal", Icon: Package },
};

const ACTION_VERB: Record<MerchantActivityAction, string> = {
  created: "Added",
  updated: "Updated",
  deleted: "Removed",
};

// Mirrors the green/blue/red convention already used for points/stamps deltas in
// ActivityTab.tsx's user-activity row.
const ACTION_COLOR: Record<MerchantActivityAction, string> = {
  created: "text-green-600",
  updated: "text-blue-500",
  deleted: "text-red-500",
};

function getMerchantActivityLabel(activity: MerchantActivity): string {
  const { entity_type, action, entity_name } = activity;
  const config = ENTITY_CONFIG[entity_type];
  const noun = config?.noun ?? entity_type;
  const verb = ACTION_VERB[action] ?? action;
  if (SINGLETON_ENTITY_TYPES.has(entity_type) || !entity_name) {
    return `${verb} ${noun}`;
  }
  return `${verb} ${noun} "${entity_name}"`;
}

type MerchantActivityTabProps = {
  storeId: string;
};

export function MerchantActivityTab({ storeId }: MerchantActivityTabProps) {
  const user = useAdminAuth();
  const [offset, setOffset] = useState(0);
  const [allItems, setAllItems] = useState<MerchantActivity[]>([]);
  const [total, setTotal] = useState<number | null>(null);

  const { data, isPending, isFetching } = useQuery({
    queryKey: ["merchant-activity", storeId, offset],
    queryFn: () => getMerchantActivity(user!, storeId, { limit: PAGE_SIZE, offset }),
    enabled: !!user,
    refetchOnMount: "always",
  });

  // Synced from `data`, not inside queryFn, so a cache hit on remount still repopulates state.
  useEffect(() => {
    if (!data) return;
    if (offset === 0) {
      setAllItems(data.data);
    } else {
      setAllItems((prev) => [...prev, ...data.data]);
    }
    setTotal(data.total);
  }, [data, offset]);

  function handleLoadMore() {
    setOffset((prev) => prev + PAGE_SIZE);
  }

  const hasMore = total !== null && allItems.length < total;

  return (
    <div className="space-y-4">
      {total !== null && allItems.length > 0 && (
        <p className="text-sm text-muted-foreground">{formatInt(total)} total changes</p>
      )}

      {isPending && allItems.length === 0 ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-5 border-primary" />
        </div>
      ) : allItems.length === 0 ? (
        <EmptyState
          title="No store activity yet"
          description="Menu, reward, event, and staff changes made by the merchant or on their behalf will appear here."
        />
      ) : (
        <div className="rounded-lg border divide-y">
          {allItems.map((activity) => (
            <MerchantActivityRow key={activity.id} activity={activity} />
          ))}
        </div>
      )}

      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" size="sm" onClick={handleLoadMore} disabled={isFetching}>
            {isFetching ? (
              <>
                <Spinner className="size-4 border-primary mr-2" />
                Loading...
              </>
            ) : (
              "Load more"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Local sub-component (single call site — kept in this file) ───────────────

function MerchantActivityRow({ activity }: { activity: MerchantActivity }) {
  const config = ENTITY_CONFIG[activity.entity_type];
  const Icon = config?.Icon ?? Store;

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className={`shrink-0 rounded-full bg-muted p-2 ${ACTION_COLOR[activity.action]}`}>
        <Icon className="size-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{getMerchantActivityLabel(activity)}</p>
        <ActorLine activity={activity} />
      </div>
      <p className="shrink-0 text-xs text-muted-foreground">
        {formatDateTime(activity.created_at)}
      </p>
    </div>
  );
}

function ActorLine({ activity }: { activity: MerchantActivity }) {
  const { actor_type, actor_name } = activity;

  if (actor_type === "system") return null;

  if (actor_type === "admin") {
    return (
      <p className="text-xs text-muted-foreground">
        <Badge variant="secondary">Disca Support</Badge>
      </p>
    );
  }

  if (!actor_name) return null;

  return (
    <p className="text-xs text-muted-foreground">
      by {actor_name} ({actor_type})
    </p>
  );
}
